import { Link } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const PUBLISHER = 'HeroRocket Interactive LLC';
const CONTACT = 'jhay.davis@herorocketgames.com';
const LAST_UPDATED = 'February 1, 2026';
const POLICY_VERSION = '2026.02.01';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-20" data-testid={`privacy-section-${id}`}>
    <h2
      className="text-2xl font-light text-zinc-100 mb-3"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
    >
      {title}
    </h2>
    <div className="text-sm text-zinc-300 leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid="privacy-page">
      <div className="overlay-noise" />

      <header className="relative z-10 border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="block">
            <h1
              className="text-2xl font-light tracking-tight text-zinc-100"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              SAGE
            </h1>
            <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
              Privacy Policy
            </p>
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle className="border border-white/20 rounded-sm" />
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1 w-full">
        <article className="max-w-[720px] mx-auto px-6 py-12">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">
            Last updated: {LAST_UPDATED} &middot; Version {POLICY_VERSION}
          </p>
          <h1
            className="text-4xl sm:text-5xl font-light text-zinc-100 mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-10">
            SAGE is a personalized AI coaching platform published by{' '}
            {PUBLISHER}. We care about keeping your material yours. This
            policy explains, in plain English, what we collect, why we
            collect it, and the choices you have. Questions:{' '}
            <a
              href={`mailto:${CONTACT}`}
              className="underline text-zinc-200 hover:text-white"
            >
              {CONTACT}
            </a>
            .
          </p>

          <Section id="not-therapy" title="This isn't therapy or medical advice">
            <p>
              SAGE is a coaching tool, not a licensed healthcare provider.
              Anything you share is coaching context — not a medical
              record. If you're in crisis, please contact a qualified
              human: call or text <strong>988</strong> in the United
              States, or visit{' '}
              <a
                href="https://findahelpline.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-zinc-100 hover:text-white"
              >
                findahelpline.com
              </a>
              {' '}internationally.
            </p>
          </Section>

          <Section id="what-we-collect" title="What personal data we collect">
            <p>Depending on how you use SAGE, we may collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Your email address (always).</li>
              <li>Your first name (optional — used in greetings).</li>
              <li>Authentication tokens that identify your signed-in session.</li>
              <li>
                Materials you upload — Knowledge Anchors, Reference
                Documents, session audio, Learn Mode content.
              </li>
              <li>
                Session audio and transcripts for the coaching sessions you
                run.
              </li>
              <li>Your voice and theme preferences.</li>
              <li>
                Semantic and episodic memory records that let SAGE
                remember you across sessions.
              </li>
              <li>
                Usage telemetry: login count, active days, coaching session
                count, tutorial-completion flag.
              </li>
            </ul>
          </Section>

          <Section id="how-we-use-it" title="How we use each category">
            <p>
              Each category is used <strong>strictly</strong> to deliver
              the service and to surface <em>your own content</em> back to{' '}
              <em>you</em>:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Email — authentication, transactional messages, support replies.</li>
              <li>Name — personalized greetings inside SAGE.</li>
              <li>
                Uploads, session audio, transcripts, memory — to let the
                coach meet you where you are and remember context across
                sessions.
              </li>
              <li>Voice and theme preferences — to render SAGE the way you like it.</li>
              <li>
                Telemetry — to understand how SAGE is being used so we can
                improve it.
              </li>
            </ul>
            <p>
              We do <strong>not</strong> use your content to train
              third-party or shared models, and we do <strong>not</strong>{' '}
              use it to market third-party products.
            </p>
          </Section>

          <Section id="where-it-lives" title="Where your data lives">
            <ul className="list-disc pl-6 space-y-1">
              <li>Primary storage: MongoDB, operated by SAGE.</li>
              <li>Transactional email delivery: Mailgun.</li>
              <li>Voice generation: ElevenLabs.</li>
              <li>Text responses: Google Gemini.</li>
              <li>Wearable data (optional): Oura, only if you connect your ring.</li>
            </ul>
            <p>
              Data in transit uses HTTPS. Each third-party service
              receives only the content it needs to perform its specific
              job.
            </p>
          </Section>

          <Section id="oura" title="Oura ring data (optional)">
            <p>
              If you choose to connect an Oura ring, SAGE gets{' '}
              <strong>read-only</strong> access to your daily summaries. It
              can't change anything in your Oura account. SAGE reads the last
              few days of:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Sleep score, time asleep and sleep efficiency.</li>
              <li>Readiness score and temperature deviation.</li>
              <li>Overnight heart rate variability and resting heart rate.</li>
              <li>Steps and activity score.</li>
              <li>Daytime stress, recovery and resilience level.</li>
            </ul>
            <p>
              We use it only to show the Body Signals card on your
              Dashboard, to give SAGE brief context so it can gently factor
              in how rested you are (shared with Google Gemini and ElevenLabs
              as part of the coaching prompt), and to add a one-line note to
              your session summaries. It is never treated as medical advice,
              never sold and never used to train models. Your Oura sign-in
              tokens are stored encrypted.
            </p>
            <p>
              To delete it, tap <strong>Disconnect &amp; delete Oura data</strong>{' '}
              on the Body Signals card. This revokes SAGE's access and
              permanently removes all stored Oura data, including the notes
              in past session summaries. You can also revoke access any time
              from your Oura account settings.
            </p>
          </Section>

          <Section id="retention" title="Retention">
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Session data (audio, transcripts, summaries, uploads)
                persists until you delete it or you delete your account.
              </li>
              <li>Auto-Mode minute usage resets monthly.</li>
              <li>
                Security audit events are retained for the most recent 100
                entries per filter.
              </li>
            </ul>
          </Section>

          <Section id="deletion" title="Deletion rights">
            <p>
              You can delete uploads and sessions from inside SAGE. You
              can delete your whole account by writing to{' '}
              <a
                href={`mailto:${CONTACT}`}
                className="underline text-zinc-200 hover:text-white"
              >
                {CONTACT}
              </a>
              . After deletion, there is a short backup propagation window
              (typically a few days) before copies are fully purged from
              backups.
            </p>
          </Section>

          <Section id="children" title="Children">
            <p>
              SAGE is for adults 18+. We do not knowingly collect data
              from anyone under 18. If you believe a minor has signed up,
              write to{' '}
              <a
                href={`mailto:${CONTACT}`}
                className="underline text-zinc-200 hover:text-white"
              >
                {CONTACT}
              </a>
              {' '}and we will remove the account.
            </p>
          </Section>

          <Section id="cookies" title="Cookies and local storage">
            <p>
              SAGE uses two pieces of local client storage:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Your authentication token (currently in{' '}
                <code className="text-zinc-100">localStorage</code>; we
                plan to move it to an HttpOnly cookie).
              </li>
              <li>Your theme preference (dark or light).</li>
            </ul>
            <p>
              SAGE does <strong>not</strong> run third-party analytics or
              tracking cookies.
            </p>
          </Section>

          <Section id="security" title="How we protect your data">
            <p>In plain English, here's what we do:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>JWT-based authentication on every API call.</li>
              <li>Server-side token storage for OAuth integrations.</li>
              <li>Upload magic-byte validation to reject disguised files.</li>
              <li>Antivirus scanning on uploads.</li>
              <li>Prompt-injection sanitization on user text that reaches the model.</li>
              <li>PII redaction at prompt-assembly time.</li>
              <li>A security audit log that records sensitive events.</li>
              <li>HTTPS on all transit.</li>
            </ul>
          </Section>

          <Section id="international" title="Where SAGE operates from">
            <p>
              SAGE operates from the <strong>United States</strong>. If
              you use SAGE from outside the U.S., your data is processed
              and stored in the U.S. By using SAGE from outside the U.S.
              you consent to that transfer.
            </p>
          </Section>

          <Section id="california" title="California Residents' Rights">
            <p>
              If you are a California resident, the California Consumer
              Privacy Act (CCPA) gives you specific rights regarding your
              personal information. SAGE honors all of them:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Right to know</strong> what personal information
                we collect, use, disclose, and the categories of sources.
              </li>
              <li>
                <strong>Right to delete</strong> personal information we
                hold about you (subject to limited exceptions required by
                law).
              </li>
              <li>
                <strong>Right to correct</strong> inaccurate personal
                information.
              </li>
              <li>
                <strong>Right to opt out of sale or sharing</strong> of
                personal information. SAGE does <strong>not</strong> sell
                or share your personal information.
              </li>
              <li>
                <strong>Right to limit use of sensitive personal information</strong>{' '}
                to the uses required to deliver the service you requested.
              </li>
              <li>
                <strong>Right to non-discrimination</strong> — we won't
                treat you differently for exercising any of these rights.
              </li>
            </ul>
            <p>
              To exercise any of these rights, write to{' '}
              <a
                href={`mailto:${CONTACT}?subject=CCPA%20request`}
                className="underline text-zinc-200 hover:text-white"
                data-testid="privacy-ccpa-email"
              >
                {CONTACT}
              </a>
              . Please include enough detail that we can verify your
              identity and locate your account.
            </p>
          </Section>

          <Section id="changes" title="Changes to this policy">
            <p>
              When we change this policy in a way that matters to you,
              we'll update the "Last updated" date, bump the version, and
              ask you to re-acknowledge on your next sign-in.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions about this policy, deletion requests, or CCPA
              requests:{' '}
              <a
                href={`mailto:${CONTACT}`}
                className="underline text-zinc-200 hover:text-white"
                data-testid="privacy-contact-email"
              >
                {CONTACT}
              </a>
            </p>
          </Section>

          <div className="mt-12 pt-6 border-t border-white/5 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500">
            <Link to="/terms" className="hover:text-zinc-300 transition-colors">
              ← Terms of Use
            </Link>
            <Link to="/" className="hover:text-zinc-300 transition-colors">
              Back to SAGE →
            </Link>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
